import { effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  catchErrorAsync,
  clone,
  decycle,
  isFeatureEnabled,
  sleep
} from '@approot/utilities';
import buildNumber from '@root/build_number';
import { environment } from '@root/environments/environment';
import { chain } from 'lodash';
import { Once, Throttle } from 'lodash-decorators';
import { pick } from 'lodash/fp';
import { Channel, Presence, Socket } from 'phoenix';
import { AlertsService } from '../alerts/alerts.service';
import { AuthService, UserSession } from '../auth/auth.service';
import { ConfigService } from '../config/config.service';
import { UserStorageService } from '../user-storage/user-storage.service';
import { PhoenixEventBusService } from './phoenix-event-bus.service';
import { getToken, join, joinChannel, leave } from './phoenix.utils';

export type GetEventState = {
  ago: number;
  session_id: string;
  timestamp: number;
  diff: number;
  loggedIn: boolean;
  userRole: 'visible' | 'hidden';
  visibility: string;
};

export type PresenceUser = {
  email: string;
  group: string;
  username: string;
  online_at: number;
  online_at_str: string;
  session_id: string;
  user_agent: string;
  user_agent_hash: string;
  build_number: string;
  old_build: boolean;
  sso: boolean;
  phx_ref: string;
  auth_time: number;
  auth_time_str: string;
  get_events_state: GetEventState;
  intensity?: number;
  medusaUser?: string;
  remote_ip?: string;
};

export type PresenceStateEntry = {
  metas: PresenceUser[];
};

export type PresenceState = Record<string, PresenceStateEntry>;

export type PresenceStateDiff = {
  joins: PresenceState;
  leaves: PresenceState;
};

export type MonitorConfiguration = {
  market_data_sources: {
    enabled: boolean;
    parents: string[];
  };
};

type UserMeta = {
  email: string;
  sub: string;
  superuser: boolean;
  sessionId: string;
  group: string;
};

export type ConnectedChannels = Record<string, Channel>;

export type CallbacksAndParams = {
  params?: Record<string, any>;
  onShout?: (payload: unknown) => void;
  onJoin?: (payload: unknown) => void;
};

export const DefaultGetEventsState = {
  diff: null,
  loggedIn: false,
  medusaUser: null,
  medusaEntity: null,
  visibility: 'visible'
};

@Injectable({ providedIn: 'root' })
export class PhoenixService implements OnDestroy {
  public readonly onlineUsers = signal<PresenceUser[]>([]);
  public connectedChannels: ConnectedChannels = {};
  public readonly connected = signal<boolean>(false);
  public readonly getEventsPoll = signal<any>({});
  public readonly getEventsStatePoll = signal<any>({});

  private readonly authService = inject(AuthService);
  private readonly alerts = inject(AlertsService);
  private readonly userPrefs = inject(UserStorageService);
  private readonly config = inject(ConfigService);
  private readonly eventBus = inject(PhoenixEventBusService);
  private socket: Socket;
  private userMeta: UserMeta = {
    email: '',
    sub: '',
    superuser: false,
    sessionId: window.sessionId,
    group: null
  };
  private tokenErrors = 0;
  private lastConnectionAttempt = 0;
  private lobby: Channel;
  private lastToken: string = null;
  private enableLogEventsToPhx = true;
  private eventBusSequenceId = -1;

  public constructor() {
    if (!this.isEnabled()) {
      return;
    }

    effect(async () => {
      if (this.connected()) {
        this.joinLobby();
        this.connectedChannels = await this.joinUserChannels();
      }
    });

    effect(() => {
      const settings = this.config.userSettings();
    });

    this.eventBus.queue.subscribe({
      next: msg => {
        const seqId = msg.sequenceId;
        if (seqId <= this.eventBusSequenceId) {
          console.log('Skipping duplicate event', msg);
          return;
        }
        this.eventBusSequenceId = seqId;
        this.logEvent(msg);
      }
    });

    this.connectionPoller();
    console.log('PhoenixService created');
  }

  public ngOnDestroy(): void {
    console.log('PhoenixService destroyed');
  }

  public async testEvent() {
    await this.waitForPhx();
    this.logEvent({ type: 'test', message: 'test event' });
  }

  public leave(channel: Channel) {
    if (!channel) {
      return;
    }
    channel.leave();
    delete this.connectedChannels[channel.topic];
    console.log(`Left %c${channel.topic}`, `color: blue`);
  }

  public getChannel(topic: string): Channel | null {
    const ch = this.connectedChannels[topic];
    if (!ch) {
      return null;
    }
    if (!['joined', 'joining'].includes(ch.state)) {
      delete this.connectedChannels[topic];
      return null;
    }
    return ch;
  }

  public async connect() {
    if (!this.isEnabled()) {
      return;
    }
    this.disconnect();
    this.tryConnect();
  }

  public joinChannel(
    channelName: string,
    callbacks?: CallbacksAndParams
  ): Channel {
    if (!this.socket) {
      console.error('Socket not connected');
      return null;
    }
    const params = callbacks?.params || {};
    const channel = this.socket.channel(channelName, {
      ...pick(['email', 'sub'])(this.userMeta),
      ...params
    });

    this.connectedChannels[channelName] = channel;

    joinChannel(channel, channelName, callbacks);

    return channel;
  }

  public async waitForPhx() {
    // If not already connected, this usually connects in 1-2 tries
    let i = 0;
    while (!this.connected()) {
      let delay = 100;
      if (i > 10) {
        if (i % 10 === 0) {
          console.log(`Waiting for Phoenix: try ${i}`);
        }
        delay = 400;
      } else if (i > 5) {
        console.log(`Waiting for Phoenix: try ${i}`);
        delay = 200;
      } else if (i > 2) {
        // noop - don't log 1st few tries
      }
      await sleep(delay);
    }
  }

  public async sendToLobby(topic: string, event: any, timeout = 5_000) {
    await this.waitForPhx();
    this.lobby?.push(topic, event, timeout);
  }

  public async logEvent(payload: any, timeout = 30_000) {
    if (!this.enableLogEventsToPhx) {
      return;
    }
    for (let i = 0; i < 100; i++) {
      if (!this.lobby) {
        await sleep(100);
      } else {
        break;
      }
    }
    if (!this.lobby) {
      return;
    }

    // Have seen occasional "max call stack exceeded"
    // I think the clone() call is redundant as decycle() returns a deep copy.
    payload = decycle(clone(payload));

    this.redactPassword(payload);

    if (!payload['type']) {
      console.warn('logEvent: type is required for', payload);
      return;
    }

    setTimeout(() => {
      const topic = payload['topic'] || 'log_event';
      try {
        this.lobby.push(
          topic,
          { ...payload, sessionId: window.sessionId },
          timeout
        );
      } catch (e) {
        console.error(e);
      }
    });
  }

  public logGetEvents(payload: object) {
    setTimeout(async () => {
      await this.waitForPhx();
      this.lobby?.push('log_get_events', payload);
    });
  }

  public sendGetEventsState(payload: object) {
    setTimeout(async () => {
      await this.waitForPhx();
      this.lobby?.push('get_events_state', payload);
    });
  }

  public disconnect() {
    if (this.socket?.isConnected()) {
      console.warn('Disconnecting from phoenix socket');
    }
    this.connectedChannels = {};
    this.clearSocket();
  }

  public isEnabled() {
    return true;
  }

  public sendSlack(message: string) {
    setTimeout(async () => {
      await this.waitForPhx();
      this.lobby?.push('slack', { message });
    }, 500);
  }

  /*<PRIVATE>*****************************************************************/

  @Once()
  private async getPublicIP() {
    function abortSignal(timeout = 1000) {
      const controller = new AbortController();
      setTimeout(() => controller.abort('Timeout'), timeout);
      return controller.signal;
    }

    async function ip() {
      const res = await fetch('https://checkip.amazonaws.com', {
        signal: abortSignal()
      });
      const data = await res.text();
      return data.trim();
    }

    const [ipaddr, error] = await catchErrorAsync(ip());
    if (error) {
      console.error(error);
    }
    return ipaddr ?? 'unknown';
  }

  private redactPassword(object: any) {
    const walkNestedObject = (obj: any) => {
      if (
        typeof obj === 'string' ||
        typeof obj === 'number' ||
        typeof obj === 'boolean'
      ) {
        return;
      }
      if (Array.isArray(obj)) {
        for (var i in obj) {
          walkNestedObject(obj[i]);
        }
      } else {
        for (var key in obj) {
          if (key === 'password' && typeof obj[key] === 'string') {
            obj[key] = '********';
          }
          if (typeof obj[key] === 'object' && obj[key] !== null) {
            walkNestedObject(obj[key]);
          }
        }
      }
    };

    walkNestedObject(object);
  }

  private connectionPoller() {
    if (!this.socket?.isConnected()) {
      this.tryConnect();
    }
    let delay = 1000;
    if (this.tokenErrors > 25) {
      delay = 10_000;
    } else if (this.tokenErrors > 10) {
      delay = 5_000;
    }

    setTimeout(() => this.connectionPoller(), delay);
  }

  /**
   * @deprecated
   */
  private async tokenUpdate() {
    if (!this.socket?.isConnected()) {
      return;
    }
    let currentSession: UserSession;
    try {
      currentSession = await this.authService.currentSession();
    } catch (e) {
      if (e === 'No current user') {
        return;
      }
      throw e;
    }

    const { token } = getToken(currentSession);
    if (token !== this.lastToken) {
      this.lobby.push('update_token', { token });
    }
    this.lastToken = token;
  }

  private clearSocket() {
    if (this.socket) {
      console.log('Clearing phoenix socket', this.socket);
      this.socket.disconnect();
      this.socket = null;
    }
    this.connected.set(false);
  }

  private async tryConnect() {
    // Occasional race condition where multiple calls to tryConnect() happen at once
    const now = Date.now();
    if (now - this.lastConnectionAttempt < 250) {
      return;
    }
    this.lastConnectionAttempt = now;

    let currentSession: UserSession;
    try {
      currentSession = await this.authService.currentSession();
    } catch (e) {
      this.clearSocket();
      if (e === 'No current user') {
        return;
      }
      throw e;
    }

    const { token, email, sub, superuser, group } = getToken(currentSession);
    const sessionToken = token;
    this.userMeta = { ...this.userMeta, email, sub, superuser, group };

    console.log(
      'Connecting to Phoenix: current state',
      this.socket?.connectionState()
    );
    const endpoint = `${environment.phxEndpoint}/socket`;

    const state = this.socket?.connectionState();
    if (state === 'open') {
      console.warn('Already connected to phoenix socket - skipping reconnect');
      return;
    }

    if (state === 'connecting' || state === 'closing') {
      setTimeout(() => this.tryConnect(), 150);
      return;
    }

    let logger = null;
    if (isFeatureEnabled('debugPhx', false)) {
      logger = this.logger.bind(this);
    }

    // Disbable phx reconnects
    const reconnectAfterMs = (_tries: number) => {
      return 24 * 3_600_000;
    };

    // Rare race condition (this line should be after the last `await` in this block)
    if (this.socket?.isConnected()) {
      console.warn('Already connected to phoenix socket - skipping reconnect');
      return;
    }
    this.socket?.disconnect();

    const remoteIP = await this.getPublicIP();

    this.socket = new Socket(endpoint, {
      params: {
        token: sessionToken,
        session_id: this.userMeta.sessionId,
        user_agent: navigator.userAgent,
        build_number: buildNumber,
        remote_ip: remoteIP
      },
      reconnectAfterMs,
      logger
    });

    this.configureSocketListeners();
    this.socket.connect();
  }

  private logger(kind: string, msg: string, data: any) {
    // Note, must be enabled in DevTools (Console -> Level -> Verbose) to see these messages
    console.debug(`Phoenix ${kind}:${msg}`, data);
  }

  private async joinUserChannels(): Promise<ConnectedChannels> {
    const email = this.joinChannel(`user:email:${this.userMeta.email}`);
    email.on('directMessage', this.onDirectMessage.bind(this));
    email.on('logout_if_hibernating', this.onLogoutIfHibernating.bind(this));
    email.on('logout', this.onLogout.bind(this));

    const sub = this.joinChannel(`user:sub:${this.userMeta.sub}`);
    sub.on('play_ping', () => this.userPrefs.ping());

    const session = this.joinChannel(`user:session:${this.userMeta.sessionId}`);
    session.on('duplicate_medusa_login', this.alertDuplicateLogins.bind(this));

    const adminChannel = (await this.isSuperuser())
      ? this.joinChannel('user:admin')
      : undefined;

    return {
      sub,
      email,
      session,
      adminChannel,
      medusa: this.joinChannel(`medusa:session:${this.userMeta.sessionId}`)
    };
  }

  private async isSuperuser(): Promise<boolean> {
    const currentSession = await this.authService.currentSession();
    const token = getToken(currentSession);
    return token.superuser;
  }

  private joinLobby() {
    const lobby = this.joinChannel('room:lobby');
    lobby.on('directMessage', this.onDirectMessage.bind(this));
    lobby.on('ping', this.onPing.bind(this, lobby));
    lobby.on('poll_get_events', this.onPollGetEvent.bind(this));
    lobby.on('poll_get_events_state', this.onPollGetEventState.bind(this));

    // the backend enforces this check too.
    if (this.userMeta.superuser) {
      this.subscribePresenceUpdates(lobby);
    }
    this.lobby = lobby;
    return lobby;
  }

  @Throttle(2 * 60_000)
  private alertDuplicateLogins(r: any) {
    if (!isFeatureEnabled('enableDuplicateLoginsAlert', true)) {
      return;
    }
    this.alerts.addAlert({
      message: `Another browser is logged in as Trading Entity ${r.medusaEntity}.
        This can lead to inconsistent behaviour. Please log out all other browsers using the same Trading Entity.
        If you have just refreshed your browser, this message might be a false positive.`,
      type: 'warning'
    });
  }

  private onPing(lobby: Channel, payload: any) {
    lobby
      .push('ping', {
        ...payload,
        localtime: Date.now(),
        visibility: document.visibilityState
      })
      .receive('ok', _ => {});
  }

  private onPollGetEvent(payload: any) {
    this.getEventsPoll.set(payload);
  }

  private onPollGetEventState(payload: any) {
    this.getEventsStatePoll.set(payload);
  }

  private onLogoutIfHibernating(_payload: any) {
    if (document.visibilityState === 'hidden') {
      this.authService.logout();
    }
  }

  private onLogout(_payload?: any) {
    this.authService.logout();
  }

  private onDirectMessage(payload: any) {
    console.log('Inbound direct message', payload);
    if (payload.message) {
      const disableTimeOut =
        payload.disableTimeOut == null ? true : payload.disableTimeOut;
      const title =
        payload.from != null ? `From sysadmin: ${payload.from}` : undefined;
      this.alerts.addAlert({
        message: payload.message,
        title,
        type: payload.type || 'success',
        config: {
          disableTimeOut,
          closeButton: disableTimeOut,
          tapToDismiss: true
        }
      });
      const ping = payload.ping == null ? true : payload.ping;
      if (ping) {
        this.userPrefs.ping();
      }
    }
  }

  private subscribePresenceUpdates(lobby: Channel) {
    const presence = new Presence(lobby);

    // Note: only superusers will get presence updates
    presence.onJoin(
      (_id, current: PresenceStateEntry, newPres: PresenceStateEntry) => {
        if (current) return;
        const users = newPres.metas.map(m => m.email);
        console.log(`Presence: user joined %c${users}`, `color: green`);
      }
    );

    this.subscribeLobbyState(lobby);
    this.subscribeLobbyDiff(lobby);
  }

  private setOnlineUsers(users: PresenceUser[]): void {
    users = chain(users)
      .uniqBy('phx_ref')
      .orderBy([e => e.email.toLowerCase(), s => s.session_id], ['asc', 'desc'])
      .value();
    this.onlineUsers.set(users);
  }

  /**
   * Note: only superusers will get presence updates
   */
  private subscribeLobbyState(lobby: Channel) {
    lobby.on('presence_state', (state: PresenceState) => {
      const users = join([], state);
      this.setOnlineUsers(users);
    });
  }

  private subscribeLobbyDiff(lobby: Channel) {
    lobby.on('presence_diff', (state: PresenceStateDiff) => {
      let users = this.onlineUsers();
      users = leave(users, state.leaves);
      users = join(users, state.joins);
      this.setOnlineUsers(users);
    });
  }

  private configureSocketListeners() {
    this.socket.onClose(async () => {
      console.log(
        `%cPhoenix socket closed ${new Date().toLocaleTimeString()}`,
        'color: black; font-style: italic; background-color: red;padding: 5px'
      );
      this.connected.set(false);
    });
    this.socket.onOpen(() => {
      console.log(
        `%cPhoenix socket opened ${new Date().toLocaleTimeString()}`,
        'color: white; font-style: italic; background-color: green;padding: 5px'
      );
      this.connected.set(true);
    });
  }
}
